import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  Heart,
  Activity,
  AlertTriangle,
  ShieldCheck,
  CheckCircle,
} from 'lucide-react';

const SESSION_TYPE_COLORS = {
  TRAINING: 'border-emerald-500/30 bg-emerald-500/5',
  MATCH: 'border-rose-500/30 bg-rose-500/5',
  GYM: 'border-amber-500/30 bg-amber-500/5',
  RECOVERY: 'border-sky-500/30 bg-sky-500/5',
  RTP: 'border-orange-500/30 bg-orange-500/5',
  OTHER: 'border-zinc-500/30 bg-zinc-500/5',
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const PHASE_LABELS = {
  PHASE_1: 'Phase 1',
  PHASE_2: 'Phase 2',
  PHASE_3: 'Phase 3',
  PHASE_4: 'Phase 4',
  PHASE_5: 'Phase 5',
  FULL_INTEGRATION: 'Full Integration',
};

/* ─── Section wrapper ────────────────────────────────────────── */
const Section = ({ icon: Icon, iconColor, title, count, children, testId }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm" data-testid={testId}>
    <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
      <h2 className="font-semibold text-zinc-100 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        {title}
      </h2>
      {count !== undefined && (
        <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs font-semibold rounded tabular-nums">
          {count}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="flex items-center gap-2 py-3 text-sm text-zinc-500" data-testid="empty-state">
    <CheckCircle className="w-4 h-4 text-emerald-500/60" />
    {message}
  </div>
);

/* ─── Page ───────────────────────────────────────────────────── */
const DaySnapshotPage = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useLanguage();

  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (teamFilter !== 'all') params.append('team_id', teamFilter);
      const res = await api.get(`/planner/day-snapshot/${date}?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Snapshot fetch failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [api, date, teamFilter]);

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const prev = () => {
    const d = new Date(date);
    d.setDate(d.getDate() - 1);
    navigate(`/staff/planner/snapshot/${d.toISOString().split('T')[0]}`);
  };
  const next = () => {
    const d = new Date(date);
    d.setDate(d.getDate() + 1);
    navigate(`/staff/planner/snapshot/${d.toISOString().split('T')[0]}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  const sessions = data?.sessions || [];
  const missingWellness = data?.missing_wellness || [];
  const missingRpe = data?.missing_rpe || [];
  const highLoad = data?.high_load_athletes || [];
  const rtpAthletes = data?.rtp_athletes || [];

  return (
    <div className="space-y-6 fade-in" data-testid="day-snapshot-page">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button
          onClick={() => navigate('/staff/planner')}
          variant="ghost"
          size="icon"
          className="text-zinc-400 hover:text-zinc-100 self-start shrink-0"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-100" data-testid="snapshot-title">
            Day Snapshot
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <div className="flex items-center bg-zinc-900 border border-zinc-800 rounded-sm">
              <Button onClick={prev} variant="ghost" size="icon" className="text-zinc-400 hover:text-zinc-100 h-8 w-8" data-testid="prev-day">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-2 sm:px-3 text-xs sm:text-sm font-medium text-zinc-100">
                <Calendar className="w-4 h-4 inline mr-1 sm:mr-2" />
                {formatDate(date)}
              </span>
              <Button onClick={next} variant="ghost" size="icon" className="text-zinc-400 hover:text-zinc-100 h-8 w-8" data-testid="next-day">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-full sm:w-[180px] bg-zinc-950 border-zinc-800 rounded-sm" data-testid="team-filter">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">{t('all')} {t('teams')}</SelectItem>
            {teams.map(tm => (
              <SelectItem key={tm.id} value={tm.id}>{tm.code}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── 5 Sections: 2-col on desktop, stacked on mobile ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* 1. Today's Sessions */}
        <Section icon={Calendar} iconColor="text-amber-400" title="Today's Sessions" count={sessions.length} testId="section-sessions">
          {sessions.length === 0 ? (
            <EmptyState message="No sessions scheduled for today." />
          ) : (
            <div className="space-y-2">
              {sessions.map(s => (
                <Link
                  key={s.id}
                  to={`/staff/planner/session/${s.id}`}
                  className={`block p-3 rounded-sm border transition-colors hover:bg-zinc-800/30 ${SESSION_TYPE_COLORS[s.type] || SESSION_TYPE_COLORS.OTHER}`}
                  data-testid={`session-card-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-zinc-100 truncate">
                        {s.start_time ? <span className="text-amber-400/80">{s.start_time} · </span> : null}
                        {s.team} — {s.title || s.type}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{s.type}</div>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-zinc-400 shrink-0 ml-3">
                      <Users className="w-3.5 h-3.5" />
                      {s.assigned_athletes}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* 2. Missing Wellness */}
        <Section icon={Heart} iconColor={missingWellness.length > 0 ? 'text-rose-400' : 'text-emerald-400'} title="Missing Wellness" count={missingWellness.length} testId="section-missing-wellness">
          {missingWellness.length === 0 ? (
            <EmptyState message="All athletes submitted wellness today." />
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {missingWellness.map(a => (
                <div key={a.athlete_id} className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-zinc-800/30" data-testid={`missing-wellness-${a.athlete_id}`}>
                  <span className="text-sm text-zinc-200 truncate">{a.athlete_name}</span>
                  <span className="text-xs text-zinc-500 shrink-0 ml-2">{a.team}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 3. Missing RPE */}
        <Section icon={Activity} iconColor={missingRpe.length > 0 ? 'text-amber-400' : 'text-emerald-400'} title="Missing RPE" count={missingRpe.length} testId="section-missing-rpe">
          {missingRpe.length === 0 ? (
            <EmptyState message="No missing RPE submissions." />
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {missingRpe.map((a, i) => (
                <div key={`${a.athlete_id}-${i}`} className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-zinc-800/30" data-testid={`missing-rpe-${a.athlete_id}`}>
                  <div className="min-w-0">
                    <span className="text-sm text-zinc-200 truncate block">{a.athlete_name}</span>
                    {a.session_ref && <span className="text-xs text-zinc-600 truncate block">{a.session_ref}</span>}
                  </div>
                  <span className="text-xs text-zinc-500 shrink-0 ml-2">{a.team}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 4. High Load Athletes */}
        <Section icon={AlertTriangle} iconColor={highLoad.length > 0 ? 'text-orange-400' : 'text-zinc-500'} title="High Load Athletes" count={highLoad.length} testId="section-high-load">
          {highLoad.length === 0 ? (
            <EmptyState message="No high load athletes today." />
          ) : (
            <div className="space-y-1.5">
              {highLoad.map(a => (
                <div key={a.athlete_id} className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-zinc-800/30" data-testid={`high-load-${a.athlete_id}`}>
                  <div className="min-w-0">
                    <span className="text-sm text-zinc-200 truncate block">{a.athlete_name}</span>
                    <span className="text-xs text-zinc-500">{a.team}</span>
                  </div>
                  <span className="text-sm font-semibold text-orange-400 tabular-nums shrink-0 ml-2">{a.load}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 5. RTP Athletes — full width on desktop */}
        <div className="lg:col-span-2">
          <Section icon={ShieldCheck} iconColor="text-purple-400" title="RTP Athletes" count={rtpAthletes.length} testId="section-rtp-athletes">
            {rtpAthletes.length === 0 ? (
              <EmptyState message="No athletes currently in RTP." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {rtpAthletes.map(a => (
                  <Link
                    key={a.profile_id}
                    to="/staff/rtp"
                    className="flex items-center justify-between p-3 rounded-sm border border-purple-500/20 bg-purple-500/5 hover:bg-purple-500/10 transition-colors"
                    data-testid={`rtp-card-${a.athlete_id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-200 truncate">{a.athlete_name}</div>
                      <div className="text-xs text-zinc-500">{a.team}</div>
                    </div>
                    <span className="text-xs text-purple-400 shrink-0 ml-2">{PHASE_LABELS[a.phase] || a.phase}</span>
                  </Link>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default DaySnapshotPage;
